import React, { useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import "./user-detail-modern.css";
import BackButton from "../Navigation/BackButton";
import Loader from "../Loader/Loader";
import NoDataConstruction from "../EmptyStates/NoDataConstruction";
import AIDocumentUpload from "../Documents/AIDocumentUpload";
import PdfViewer from "../PdfViewer/PdfViewer";
import { isOwnerOrFinance } from "../../utils/role-check";

function UserDetailViewModern({
  user,
  auth,
  editableUser,
  setEditableUser,
  isEditing,
  setIsEditing,
  loading,
  error,
  previewImage,
  documents,
  file,
  setFile,
  showUploadForm,
  setShowUploadForm,
  documentCategory,
  setDocumentCategory,
  expires,
  setExpiresAt,
  baseUrl,
  onDocumentUpload,
  onDeleteUserDocument,
  onSaveClick,
  editingDocId,
  editedExpiry,
  setEditedExpiry,
  editedCategory,
  setEditedCategory,
  handleEditExpiry,
  handleSaveExpiry,
  handleCancelEditExpiry,
  projects = []
}) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('info');
  const [showImageModal, setShowImageModal] = useState(false);
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [selectedPdfName, setSelectedPdfName] = useState("");

  if (loading) return <Loader message={t('common.loading')} />;
  if (error) return <NoDataConstruction text={t('common.error')}/>;
  if (!user) return <NoDataConstruction text={t('users.notFound')}/>;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditableUser((prev) => ({ ...prev, [name]: value }));
  };

  // Helper function to normalize document category for comparison
  const normalizeDocCategory = (category) => {
    if (!category) return '';
    return category.toLowerCase().replace(/[\s\-_]/g, '');
  };

  const getDocumentStatus = (documents) => {
    const requiredTypes = [
      { type: "vca diploma", normalized: "vcadiploma" },
      { type: "kvk-uittreksel", normalized: "kvkuittreksel" },
      { type: "verzekeringspolis", normalized: "verzekeringspolis" },
    ];
    const now = new Date();
    let hasMissing = false;
    let hasExpired = false;

    for (const req of requiredTypes) {
      const docs = (documents || []).filter((doc) =>
        normalizeDocCategory(doc.document_category) === req.normalized
      );
      if (docs.length === 0) {
        hasMissing = true;
        continue;
      }
      const hasValid = docs.some((doc) => {
        const exp = doc.expires_at ? new Date(doc.expires_at) : null;
        if (!exp) return true;
        return exp >= now;
      });
      if (!hasValid) {
        hasExpired = true;
      }
    }

    if (hasMissing) return "missing";
    if (hasExpired) return "expired";
    return "valid";
  };

  const documentStatus = getDocumentStatus(documents);

  // Filter projects by end_date (same logic as ProjectsContainer)
  const now = new Date();
  now.setHours(0, 0, 0, 0);

  const activeProjects = projects?.filter(p => {
    // If no end_date, project is active
    if (!p.end_date) return true;
    const endDate = new Date(p.end_date);
    endDate.setHours(0, 0, 0, 0);
    return endDate > now; // Active if end date is in the future
  }) || [];

  const archivedProjects = projects?.filter(p => {
    // If no end_date, project is not archived
    if (!p.end_date) return false;
    const endDate = new Date(p.end_date);
    endDate.setHours(0, 0, 0, 0);
    return endDate <= now; // Archived if end date is today or past
  }) || [];

  return (
    <div className="user-detail-modern">
      <BackButton label={t('users.title')} />

      {/* Modern Header */}
      <div className="user-header">
        <div className="user-header-main">
          <div className="user-avatar" onClick={() => previewImage && setShowImageModal(true)}>
            {previewImage ? (
              <img src={previewImage} alt={user.name} className="user-avatar-img" />
            ) : (
              <div className="user-avatar-placeholder">
                <span aria-hidden="true">👤</span>
              </div>
            )}
          </div>

          <div className="user-header-info">
            {isEditing && isOwnerOrFinance(auth?.role) ? (
              <input
                type="text"
                name="name"
                value={editableUser.name}
                onChange={handleInputChange}
                className="user-name-input"
              />
            ) : (
              <h2>{user.name}</h2>
            )}

            <div className="user-meta">
              <span className="user-role">
                <span aria-hidden="true">💼</span> {user.role?.charAt(0).toUpperCase() + user.role?.slice(1)}
              </span>
              <span className={`user-status ${user.is_active ? 'active' : 'inactive'}`}>
                <span aria-hidden="true">{user.is_active ? '✓' : '✕'}</span>
                {user.is_active ? t('profile.active') : t('profile.inactive')}
              </span>
              <span className={`doc-status ${documentStatus}`}>
                <span aria-hidden="true">📄</span>
                {documentStatus === 'valid' ? t('documents.valid') :
                 documentStatus === 'expired' ? t('documents.expired') :
                 t('documents.missing')}
              </span>
            </div>
          </div>
        </div>

        {isOwnerOrFinance(auth?.role) && (
          <div className="user-header-actions">
            {!isEditing ? (
              <button className="btn btn-edit" onClick={() => setIsEditing(true)}>
                {t('common.edit')}
              </button>
            ) : (
              <>
                <button className="btn btn-save" onClick={() => onSaveClick(editableUser)}>
                  {t('common.save')}
                </button>
                <button className="btn btn-cancel" onClick={() => setIsEditing(false)}>
                  {t('common.cancel')}
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Image Modal */}
      {showImageModal && previewImage && (
        <div className="image-modal-overlay" onClick={() => setShowImageModal(false)}>
          <button className="image-modal-close" onClick={() => setShowImageModal(false)}>
            ✕
          </button>
          <img src={previewImage} alt={user.name} className="image-modal-img" />
        </div>
      )}

      {/* Desktop Tabs */}
      <div className="tabs-container desktop-tabs">
        <button className={`tab ${activeTab === 'info' ? 'active' : ''}`} onClick={() => setActiveTab('info')}>
          {t('users.userDetails')}
        </button>
        <button className={`tab ${activeTab === 'documents' ? 'active' : ''}`} onClick={() => setActiveTab('documents')}>
          {t('documents.title')} <span className="tab-count">({documents?.length || 0})</span>
        </button>
        <button className={`tab ${activeTab === 'projects' ? 'active' : ''}`} onClick={() => setActiveTab('projects')}>
          {t('common.projects')} <span className="tab-count">({projects?.length || 0})</span>
        </button>
      </div>

      {/* Mobile Tab Selector */}
      <select
        className="tab-selector mobile-tabs"
        value={activeTab}
        onChange={(e) => setActiveTab(e.target.value)}
        aria-label={t('common.selectTab')}
      >
        <option value="info">{t('users.userDetails')}</option>
        <option value="documents">{t('documents.title')} ({documents?.length || 0})</option>
        <option value="projects">{t('common.projects')} ({projects?.length || 0})</option>
      </select>

      {/* Tab Content */}
      <div className="tab-content">
        {/* Info Tab */}
        {activeTab === 'info' && (
          <div className="info-grid">
            <div className="info-card">
              <h3>{t('profile.personalInfo')}</h3>
              <div className="info-rows">
                <div className="info-row">
                  <label>{t('common.email')}:</label>
                  {isEditing ? (
                    <input name="email" value={editableUser.email} onChange={handleInputChange} className="form-input" />
                  ) : (
                    <span className="info-value">
                      {user.email ? <a href={`mailto:${user.email}`}>{user.email}</a> : '-'}
                    </span>
                  )}
                </div>
                <div className="info-row">
                  <label>{t('common.phone')}:</label>
                  {isEditing ? (
                    <input name="phone" value={editableUser.phone} onChange={handleInputChange} className="form-input" />
                  ) : (
                    <span className="info-value">
                      {user.phone ? <a href={`tel:${user.phone}`}>{user.phone}</a> : '-'}
                    </span>
                  )}
                </div>
                <div className="info-row">
                  <label>{t('common.address')}:</label>
                  {isEditing ? (
                    <input name="address" value={editableUser.address} onChange={handleInputChange} className="form-input" />
                  ) : (
                    <span className="info-value">{user.address || '-'}</span>
                  )}
                </div>
              </div>
            </div>

            <div className="info-card">
              <h3>{t('profile.companyInfo')}</h3>
              <div className="info-rows">
                <div className="info-row">
                  <label>{t('profile.companyName')}:</label>
                  {isEditing ? (
                    <input name="company_name" value={editableUser.company_name} onChange={handleInputChange} className="form-input" />
                  ) : (
                    <span className="info-value">{user.company_name || '-'}</span>
                  )}
                </div>
                <div className="info-row">
                  <label>{t('profile.kvkNumber')}:</label>
                  {isEditing ? (
                    <input name="kvk_number" value={editableUser.kvk_number} onChange={handleInputChange} className="form-input" />
                  ) : (
                    <span className="info-value">{user.kvk_number || '-'}</span>
                  )}
                </div>
                <div className="info-row">
                  <label>{t('profile.vatNumber')}:</label>
                  {isEditing ? (
                    <input name="vat_number" value={editableUser.vat_number} onChange={handleInputChange} className="form-input" />
                  ) : (
                    <span className="info-value">{user.vat_number || '-'}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Documents Tab */}
        {activeTab === 'documents' && (
          <div className="documents-section">
            {isOwnerOrFinance(auth?.role) && (
              <button className="btn btn-add mb-4" onClick={() => setShowUploadForm(!showUploadForm)}>
                {t('documents.uploadDocument')}
              </button>
            )}

            {showUploadForm && (
              <div className="upload-card mb-6">
                <AIDocumentUpload
                  userId={user.id}
                  file={file}
                  setFile={setFile}
                  documentCategory={documentCategory}
                  setDocumentCategory={setDocumentCategory}
                  expires={expires}
                  setExpiresAt={setExpiresAt}
                  onDocumentUpload={onDocumentUpload}
                  onCancel={() => setShowUploadForm(false)}
                />
              </div>
            )}

            {documents && documents.length > 0 ? (
              <div className="documents-grid">
                {documents.map((doc) => {
                  const isExpired = doc.expires_at && new Date(doc.expires_at) < new Date();
                  const isEditingDoc = editingDocId === doc.id;
                  return (
                    <div key={doc.id} className={`document-card ${isExpired ? 'expired' : ''}`}>
                      <div className="document-header">
                        <div className="document-info">
                          {isEditingDoc ? (
                            <select
                              value={editedCategory}
                              onChange={(e) => setEditedCategory(e.target.value)}
                              className="form-select-small"
                            >
                              <option value="KvK-uittreksel">KvK-uittreksel</option>
                              <option value="VCA-diploma">VCA-diploma</option>
                              <option value="Verzekeringspolis">Verzekeringspolis</option>
                              <option value="VOG">VOG</option>
                              <option value="ID-bewijs">ID-bewijs</option>
                              <option value="Contract">Contract</option>
                              <option value="Overig">Overig</option>
                            </select>
                          ) : (
                            <h4>{doc.document_category}</h4>
                          )}
                          <p className="document-filename">{doc.file_name}</p>
                        </div>
                      </div>

                      <div className="document-expiry">
                        <span className="document-expiry-label">
                          {t('documents.expiresAt') || 'Verloopt op'}:
                        </span>
                        {isEditingDoc ? (
                          <input
                            type="date"
                            value={editedExpiry}
                            onChange={(e) => setEditedExpiry(e.target.value)}
                            className="form-input-small"
                          />
                        ) : (
                          <span className={`document-expiry-value ${isExpired ? 'expired' : ''}`}>
                            {doc.expires_at ? new Date(doc.expires_at).toLocaleDateString('nl-NL') : t('documents.notSet') || 'Niet ingesteld'}
                          </span>
                        )}
                      </div>

                      <div className="document-actions">
                        <button
                          type="button"
                          className="btn btn-view"
                          onClick={() => {
                            const pdfUrl = `${baseUrl}/uploads/users/${user.id}/${encodeURIComponent(doc.file_name)}`;
                            setSelectedPdf(pdfUrl);
                            setSelectedPdfName(doc.file_name);
                            setPdfViewerOpen(true);
                          }}
                        >
                          {t('common.view')}
                        </button>

                        {isOwnerOrFinance(auth?.role) && (
                          <>
                            {isEditingDoc ? (
                              <>
                                <button
                                  type="button"
                                  className="btn btn-save"
                                  onClick={() => handleSaveExpiry(doc.id)}
                                >
                                  {t('common.save')}
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-cancel"
                                  onClick={handleCancelEditExpiry}
                                >
                                  {t('common.cancel')}
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  className="btn btn-edit"
                                  onClick={() => handleEditExpiry(doc)}
                                >
                                  {t('common.edit')}
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-delete"
                                  onClick={() => onDeleteUserDocument(doc.id)}
                                  title={t('common.delete')}
                                >
                                  {t('common.delete')}
                                </button>
                              </>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <NoDataConstruction text={t('documents.noDocuments')} />
            )}
          </div>
        )}

        {/* Projects Tab */}
        {activeTab === 'projects' && (
          <div className="projects-section">
            {projects && projects.length > 0 ? (
              <>
                {activeProjects.length > 0 && (
                  <div className="projects-group">
                    <h3><span aria-hidden="true">✅</span> {t('projects.activeProjects')} ({activeProjects.length})</h3>
                    <div className="projects-grid">
                      {activeProjects.map(project => (
                        <Link key={project.id} to={`/projects/${project.id}`} className="project-mini-card">
                          <div className="project-mini-header">
                            <h4>{project.name}</h4>
                            <span className="status-badge status-active">{t('projects.active')}</span>
                          </div>
                          {project.location && <p className="project-location"><span aria-hidden="true">📍</span> {project.location}</p>}
                          <p className="project-role"><span aria-hidden="true">💼</span> {project.role || 'ZZP'}</p>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {archivedProjects.length > 0 && (
                  <div className="projects-group">
                    <h3><span aria-hidden="true">📦</span> {t('projects.archivedProjects')} ({archivedProjects.length})</h3>
                    <div className="projects-grid">
                      {archivedProjects.map(project => (
                        <Link key={project.id} to={`/projects/${project.id}`} className="project-mini-card archived">
                          <div className="project-mini-header">
                            <h4>{project.name}</h4>
                            <span className="status-badge status-archived">{t('projects.archived')}</span>
                          </div>
                          {project.location && <p className="project-location"><span aria-hidden="true">📍</span> {project.location}</p>}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <NoDataConstruction text={t('projects.noProjectsFound')} />
            )}
          </div>
        )}
      </div>

      {/* PDF Viewer for inline viewing */}
      {pdfViewerOpen && selectedPdf && (
        <PdfViewer
          pdfUrl={selectedPdf}
          fileName={selectedPdfName}
          onClose={() => {
            setPdfViewerOpen(false);
            setSelectedPdf(null);
            setSelectedPdfName("");
          }}
        />
      )}
    </div>
  );
}

UserDetailViewModern.propTypes = {
  user: PropTypes.object,
  auth: PropTypes.object,
  editableUser: PropTypes.object.isRequired,
  setEditableUser: PropTypes.func.isRequired,
  isEditing: PropTypes.bool.isRequired,
  setIsEditing: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  error: PropTypes.string,
  previewImage: PropTypes.string,
  documents: PropTypes.array,
  file: PropTypes.object,
  setFile: PropTypes.func.isRequired,
  showUploadForm: PropTypes.bool.isRequired,
  setShowUploadForm: PropTypes.func.isRequired,
  documentCategory: PropTypes.string.isRequired,
  setDocumentCategory: PropTypes.func.isRequired,
  expires: PropTypes.string.isRequired,
  setExpiresAt: PropTypes.func.isRequired,
  baseUrl: PropTypes.string.isRequired,
  onDocumentUpload: PropTypes.func.isRequired,
  onDeleteUserDocument: PropTypes.func.isRequired,
  onSaveClick: PropTypes.func.isRequired,
  editingDocId: PropTypes.number,
  editedExpiry: PropTypes.string,
  setEditedExpiry: PropTypes.func.isRequired,
  editedCategory: PropTypes.string,
  setEditedCategory: PropTypes.func.isRequired,
  handleEditExpiry: PropTypes.func.isRequired,
  handleSaveExpiry: PropTypes.func.isRequired,
  handleCancelEditExpiry: PropTypes.func.isRequired,
  projects: PropTypes.array,
};

export default UserDetailViewModern;

